import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Zod schema for Domain documents in Firestore
 * Path: organizations/{orgId}/domains/{domainId}
 *
 * `id` and `org_id` are denormalized onto the document body, not just implied
 * by its path: the backend's find_domain_org_id runs a collection-group query
 * across every org's domains subcollection, and Firestore can only filter on
 * document data, never on a trailing path segment. Every domain-scoped backend
 * operation depends on both being present, so they are required here to keep
 * this schema honest against core's Domain model.
 */
export const DomainSchema = z.object({
  id: z.string().min(1, "Domain ID is required"),
  org_id: z.string().min(1, "Organization ID is required"),
  url: z.string().min(1, "Domain URL is required"),
  created_at: RequiredFirestoreTimestampSchema,
  created_by: z.string().min(1, "Domain author is required"),
  updated_at: RequiredFirestoreTimestampSchema.nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

// `id` lives on DomainSchema itself now; kept as a named alias so the exported
// `Domain` type and the parse helpers below keep their current meaning.
export const DomainEntitySchema = DomainSchema;

/**
 * TypeScript type inferred from Zod schema
 * This replaces the old Domain type from src/types/Domain.ts
 */
export type DomainDoc = z.infer<typeof DomainSchema>;
export type Domain = z.infer<typeof DomainEntitySchema>;

/**
 * Parse function that validates and throws on error (strict mode)
 */
export const parseDomain = (data: unknown): Domain => {
  return DomainEntitySchema.parse(data);
};

/**
 * Safe parse function that returns success/error object
 */
export const safeDomainParse = (data: unknown) => {
  return DomainEntitySchema.safeParse(data);
};
